/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState, useEffect } from "react";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import { getLanguages, addLanguage, deleteLanguage } from "../services/languageServices";
import { getNamespaces, addNamespace, deleteNamespace } from "../services/namespaceServices";
import { isValidLanguage, isValidNamespace } from "../utility/validationHelper";
import { LANGUAGE_MAXLENGTH, NAMESPACE_MAXLENGTH } from "../../../utility/constants";

import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../utility/errorCodes";
import { ContentLayout, PanelContainer } from "../../../components/Common/PageLayouts";
import PageTabs from "../../../components/Common/PageTabs";
import { ProgressOverlay, ShowSnackbar } from "../../../components/Common";
import {
	Button,
	Card,
	CardContent,
	Divider,
	Grid,
	IconButton,
	List,
	ListItem,
	ListItemSecondaryAction,
	ListItemText,
	ListSubheader,
} from "@material-ui/core";
import { Delete } from "@material-ui/icons";

const COMPONENT_NAME = "Admin promptsManager/components/ManageSupport";

const DEFAULT_CONFIG = {
	NAMESPACES: ["System"],
	LANGUAGES: ["en"],
};

const noMessage = {
	type: "success",
	text: "",
};

const InputList = ({
	inputError,
	inpTooltip,
	inpText,
	handleInputChange,
	handleAdd,
	defaultList,
	handleDelete,
	listArr,
	maxLength,
}) => {
	const isExists = listArr.includes(inpText);
	const disableAdd = !inpText || isExists;
	const listArrSorted = listArr.sort((a, b) => (a.toUpperCase() < b.toUpperCase() ? -1 : 1));
	return (
		<Card className="w-100">
			<CardContent>
				<List
					component="nav"
					subheader={
						<ListSubheader disableSticky component="div" className="p-0">
							<Grid container justifyContent="center" spacing={2} alignItems={inputError ? "flex-start" : "center"}>
								<Grid item xs={10} lg={4}>
									<Tooltip title={inpTooltip} placement="bottom" arrow>
										<TextField
											autoComplete="new-password"
											className="w-100"
											type="text"
											margin="dense"
											variant="outlined"
											error={!!inputError}
											value={inpText}
											helperText={inputError}
											onChange={handleInputChange}
											inputProps={{
												maxLength,
											}}
										/>
									</Tooltip>
								</Grid>
								<Grid item xs={2}>
									<Button
										disabled={!inpText || isExists}
										className="w-100"
										variant="outlined"
										style={{
											backgroundColor: "#cc1f20",
											opacity: disableAdd ? 0.4 : 1,
											color: "#fff",
											borderColor: "#cc1f20",
										}}
										onClick={handleAdd}
									>
										Add
									</Button>
								</Grid>
							</Grid>
						</ListSubheader>
					}
				>
					<Grid container justifyContent="center" alignItems="center" spacing={2}>
						<Grid item xs={12} lg={6}>
							{listArrSorted.map((option, idx) => (
								<React.Fragment key={option}>
									{idx !== 0 && <Divider />}
									<ListItem button>
										<ListItemText>{option}</ListItemText>
										{!defaultList.includes(option) && (
											<ListItemSecondaryAction>
												<IconButton onClick={() => handleDelete(option)}>
													<Delete />
												</IconButton>
											</ListItemSecondaryAction>
										)}
									</ListItem>
								</React.Fragment>
							))}
						</Grid>
					</Grid>
				</List>
			</CardContent>
		</Card>
	);
};

const ManageSupport = () => {
	const [isLoading, setIsLoading] = useState(true);
	const [languageInp, setLanguageInp] = useState("");
	const [namespaceInp, setNamespaceInp] = useState("");
	const [error, setError] = useState({ lang: "", namespace: "" });
	const [languages, setLanguages] = useState([]);
	const [namespaces, setNamespaces] = useState([]);
	const [message, setMessage] = useState(noMessage);

	// Show message in snackbar
	const showMessage = (type, text) => {
		setMessage({ type, text });
	};

	const handleMessageClose = () => {
		setMessage((cur) => ({ ...cur, text: "" }));
	};

	useEffect(() => {
		const init = async () => {
			let languageArr = [];
			let namespaceArr = [];
			setIsLoading(true);
			languageArr = (await getLanguages()).sort();
			setLanguages(languageArr);

			namespaceArr = (await getNamespaces()).sort();
			setNamespaces(namespaceArr);

			setIsLoading(false);
		};

		init();
	}, []);

	// Language Functions -------------------------------------------------------
	const handleLanguageInput = (event) => {
		setLanguageInp(event.currentTarget.value);
	};

	const handleAddLanguage = async () => {
		setIsLoading(true);
		try {
			const validLanguage = await isValidLanguage(languageInp);
			if (!validLanguage) {
				setError((current) => ({ ...current, lang: `Invalid language: '${languageInp}'` }));
				return;
			}
			setError((current) => ({ ...current, lang: "" }));
			const resp = await addLanguage(languageInp);
			if (resp.status === "success") {
				setLanguages([...languages, languageInp]);
				showMessage("success", `Successfully created language ${languageInp}.`);
				setLanguageInp("");
			} else {
				showMessage("error", `Failed to create language ${languageInp}.`);
			}
		} catch (error) {
			v2.error(`Error adding prompt language`, {
				additionalData: { error: errorToObject(error), data: { languageInp } },
				componentName: COMPONENT_NAME,
				logCode: errorCodes.ADD_PROMPT_LANGUAGE_ERROR,
			});
			showMessage("error", `Failed to create language ${languageInp}.`);
		} finally {
			setIsLoading(false);
		}
	};

	const handleDeleteLanguage = async (language) => {
		setIsLoading(true);
		try {
			const resp = await deleteLanguage(language);

			if (resp.status === "success") {
				const oldList = [...languages];
				oldList.splice(languages.indexOf(language), 1);
				setLanguages(oldList);
				showMessage("success", `Successfully removed language ${language}.`);
			} else {
				showMessage("error", `Failed to remove language ${language}.`);
			}
		} catch (error) {
			v2.error(`Error deleting prompt language`, {
				additionalData: { error: errorToObject(error), data: { language } },
				componentName: COMPONENT_NAME,
				logCode: errorCodes.DELETE_PROMPT_LANGUAGE_ERROR,
			});
			showMessage("error", `Failed to remove language ${language}.`);
		} finally {
			setIsLoading(false);
		}
	};

	// Namespace Functions ------------------------------------------------------
	const handleNamespaceInput = (event) => {
		setNamespaceInp(event.currentTarget.value);
	};

	const handleAddNamespace = async () => {
		setIsLoading(true);
		try {
			const validNamespace = await isValidNamespace(namespaceInp);
			if (!validNamespace) {
				setError((current) => ({ ...current, namespace: `Invalid namespace: '${namespaceInp}'` }));
				return;
			}
			setError((current) => ({ ...current, namespace: "" }));
			const resp = await addNamespace(namespaceInp);
			if (resp.status === "success") {
				setNamespaces([...namespaces, namespaceInp]);
				showMessage("success", `Successfully created namespace ${namespaceInp}.`);
				setNamespaceInp("");
			} else {
				showMessage("error", `Failed to create namespace ${namespaceInp}.`);
			}
		} catch (error) {
			v2.error(`Error adding prompt namespace`, {
				additionalData: { error: errorToObject(error), data: { namespaceInp } },
				componentName: COMPONENT_NAME,
				logCode: errorCodes.ADD_PROMPT_NAMESPACE_ERROR,
			});
			showMessage("error", `Failed to create namespace ${namespaceInp}.`);
		} finally {
			setIsLoading(false);
		}
	};

	const handleDeleteNamespace = async (namespace) => {
		setIsLoading(true);
		try {
			const resp = await deleteNamespace(namespace);

			if (resp.status === "success") {
				const oldList = [...namespaces];
				oldList.splice(oldList.indexOf(namespace), 1);
				setNamespaces(oldList);
				showMessage("success", `Successfully removed namespace ${namespace}.`);
			} else {
				showMessage("error", `Failed to remove namespace ${namespace}.`);
			}
		} catch (error) {
			v2.error(`Error deleting prompt namespace`, {
				additionalData: { error: errorToObject(error), data: { namespace } },
				componentName: COMPONENT_NAME,
				logCode: errorCodes.DELETE_PROMPT_NAMESPACE_ERROR,
			});
			showMessage("error", `Failed to remove ${namespace}.`);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<ContentLayout>
			{isLoading && <ProgressOverlay />}
			<PanelContainer spacing={0} className="w-100">
				<PageTabs
					tabs={[
						{
							id: "languages",
							label: "Languages",
							tooltip: "Allows you to label prompts with a two-letter code identifying the prompt's language.",
							component: (
								<InputList
									handleAdd={handleAddLanguage}
									handleDelete={handleDeleteLanguage}
									handleInputChange={handleLanguageInput}
									inpText={languageInp}
									inputError={error.lang}
									defaultList={DEFAULT_CONFIG.LANGUAGES}
									inpTooltip="Enter language to add"
									listArr={languages}
									maxLength={LANGUAGE_MAXLENGTH}
								/>
							),
							default: true,
						},
						{
							id: "namespaces",
							label: "Namespaces",
							tooltip:
								"Allows prompts to be categorized by function, department, geographical location, or other grouping.",
							component: (
								<InputList
									handleAdd={handleAddNamespace}
									handleDelete={handleDeleteNamespace}
									handleInputChange={handleNamespaceInput}
									inputError={error.namespace}
									defaultList={DEFAULT_CONFIG.NAMESPACES}
									inpTooltip="Enter namespace to add"
									inpText={namespaceInp}
									listArr={namespaces}
									maxLength={NAMESPACE_MAXLENGTH}
								/>
							),
							default: false,
						},
					]}
					className="w-100"
					requireConfirmOnChange
					selectedTabInQueryString={true}
				/>
			</PanelContainer>
			<ShowSnackbar message={message.text} severity={message.type} onClose={handleMessageClose} />
		</ContentLayout>
	);
};

export default ManageSupport;
